import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import { AdminShell } from './components/AdminShell';
import { StudentShell } from './components/StudentShell';
import { AdminDashboard } from './pages/AdminDashboard';
import { AdminContentPage, AdminCourseFolderFormPage, AdminCourseFolderReportPage, AdminCourseFormPage, AdminCourseReportPage, AdminLivePage, AdminMarketingPage, AdminMaterialFormPage, AdminMaterialReportPage, AdminMiscCategoriesPage, AdminMiscCategoryFormPage, AdminMiscMaterialFormPage, AdminMiscMaterialsPage, AdminSaleDetailsPage, AdminSubjectFormPage, AdminSubjectReportPage, AdminSubscriptionFormPage, AdminSubscriptionPage, AdminUserFormPage, AdminUserReportPage, AdminVideoPage } from './pages/AdminAyurGuidePages';
import { Login } from './pages/Login';
import { StudentProfile } from './pages/StudentProfile';
import { StudentAskQuery } from './pages/StudentAskQuery';
import { StudentMyDoubts } from './pages/StudentMyDoubts';
import { StudentStaticPage } from './pages/StudentStaticPage';
import { StudentSubscribe } from './pages/StudentSubscribe';
import { StudentTransactions } from './pages/StudentTransactions';
import { StudentCertificates } from './pages/StudentCertificates';
import { StudentCertificatePrint } from './pages/StudentCertificatePrint';
import { StudentLibrary } from './pages/StudentLibrary';
import { StudentLibraryFolder } from './pages/StudentLibraryFolder';
import { StudentLibraryItem } from './pages/StudentLibraryItem';
import { StudentFreeVideos } from './pages/StudentFreeVideos';
import { StudentFreeTests } from './pages/StudentFreeTests';
import { StudentCourseDetail } from './pages/StudentCourseDetail';
import { StudentCourseTests } from './pages/StudentCourseTests';
import { StudentCoursePerformance } from './pages/StudentCoursePerformance';
import { StudentCourseStories } from './pages/StudentCourseStories';
import { LoadingState } from './components/ui';
import { StudentCourses } from './pages/StudentCourses';
import { StudentCourseFolder } from './pages/StudentCourseFolder';
import { StudentTests } from './pages/StudentTests';
import { StudentTestInfo } from './pages/StudentTestInfo';
import { StudentTestRunner } from './pages/StudentTestRunner';
import { StudentTestResult } from './pages/StudentTestResult';
import { StudentUtilityPage } from './pages/StudentUtilityPage';
import { StudentVideoPlayer } from './pages/StudentVideoPlayer';
import { StudentDownloads } from './pages/StudentDownloads';
import { StudentBookmarks } from './pages/StudentBookmarks';
import { StudentNotes } from './pages/StudentNotes';
import { StudentQuestionOfDay } from './pages/StudentQuestionOfDay';
import { StudentNotifications } from './pages/StudentNotifications';
import { StudentResultsHistory } from './pages/StudentResultsHistory';
import { StudentPerformance } from './pages/StudentPerformance';
import { TestAdminLogin } from './pages/TestAdminLogin';
import { TestAdminShell } from './components/TestAdminShell';
import { TestAdminDashboard } from './pages/TestAdminDashboard';
import { TestAdminTests } from './pages/TestAdminTests';
import { TestAdminTestEditor } from './pages/TestAdminTestEditor';
import { TestAdminQuestionBank } from './pages/TestAdminQuestionBank';
import { TestAdminQuestionImport } from './pages/TestAdminQuestionImport';
import { TestAdminQuestionForm } from './pages/TestAdminQuestionForms';
import { TestAdminMapping } from './pages/TestAdminMapping';
import { TestAdminTestQuestionReport } from './pages/TestAdminTestQuestionReport';
import { TestAdminQuestionOfDayParity, TestAdminReportedQuestionsParity, TestAdminResultsParity } from './pages/TestAdminOperationsParity';
import { TestAdminRelease } from './pages/TestAdminRelease';
import { clearStoredSession, hasValidSession } from './lib/parse';

const StudentCourseChat = lazy(() => import('./pages/StudentCourseChat').then(({ StudentCourseChat: Page }) => ({ default: Page })));
const StudentChatHub = lazy(() => import('./pages/StudentChatHub').then(({ StudentChatHub: Page }) => ({ default: Page })));

function AuthenticatedStudentShell() {
  const location = useLocation();
  const [status, setStatus] = useState<'checking' | 'authenticated' | 'unauthenticated'>('checking');

  useEffect(() => {
    let active = true;
    void hasValidSession().then((valid) => {
      if (!active) return;
      if (!valid) clearStoredSession();
      setStatus(valid ? 'authenticated' : 'unauthenticated');
    });
    return () => { active = false; };
  }, [location.pathname]);

  if (status === 'checking') return <div className="admin-auth-loading">Checking AyurGuide session…</div>;
  if (status === 'unauthenticated') return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  return <StudentShell />;
}

export default function App() {
  return <BrowserRouter><Routes>
    <Route path="/login" element={<Login />} />
    <Route path="/test-admin/login" element={<TestAdminLogin />} />
    <Route element={<AuthenticatedStudentShell />}>
      <Route path="/" element={<StudentCourses showQuestionOfDayPopup />} />
      <Route path="/courses" element={<StudentCourses showQuestionOfDayPopup />} />
      <Route path="/course-folders/:folderId" element={<StudentCourseFolder />} />
      <Route path="/courses/:courseId" element={<StudentCourseDetail />} />
      <Route path="/courses/:courseId/tests" element={<StudentCourseTests />} />
      <Route path="/courses/:courseId/performance" element={<StudentCoursePerformance />} />
      <Route path="/courses/:courseId/stories" element={<StudentCourseStories />} />
      <Route path="/courses/:courseId/chat" element={<Suspense fallback={<LoadingState />}><StudentCourseChat /></Suspense>} />
      <Route path="/chat" element={<Suspense fallback={<LoadingState />}><StudentChatHub /></Suspense>} />
      <Route path="/tests" element={<StudentTests />} />
      <Route path="/tests/:testId" element={<StudentTestInfo />} />
      <Route path="/tests/:testId/take" element={<StudentTestRunner />} />
      <Route path="/tests/result/:resultId" element={<StudentTestResult />} />
      <Route path="/search" element={<StudentCourses />} />
      <Route path="/downloads" element={<StudentDownloads />} />
      {['community', 'planner', 'revision', 'leaderboard'].map((page) => <Route key={page} path={`/${page}`} element={<StudentUtilityPage page={page} />} />)}
      <Route path="/ask-query" element={<StudentAskQuery />} />
      <Route path="/support" element={<StudentAskQuery />} />
      <Route path="/message-teacher" element={<StudentAskQuery variant="teacher" />} />
      <Route path="/my-doubts" element={<StudentMyDoubts />} />
      <Route path="/profile" element={<StudentProfile />} />
      <Route path="/faq" element={<StudentStaticPage kind="faq" />} />
      <Route path="/about" element={<StudentStaticPage kind="about" />} />
      <Route path="/terms" element={<StudentStaticPage kind="terms" />} />
      <Route path="/subscribe" element={<StudentSubscribe />} />
      <Route path="/batches" element={<StudentSubscribe />} />
      <Route path="/transactions" element={<StudentTransactions />} />
      <Route path="/certificates" element={<StudentCertificates />} />
      <Route path="/certificates/:certificateId" element={<StudentCertificatePrint />} />
      <Route path="/bookmarks" element={<StudentBookmarks />} />
      <Route path="/notes" element={<StudentNotes />} />
      <Route path="/qod" element={<StudentQuestionOfDay />} />
      <Route path="/notifications" element={<StudentNotifications />} />
      <Route path="/results" element={<StudentResultsHistory />} />
      <Route path="/performance" element={<StudentPerformance />} />
      <Route path="/library" element={<StudentLibrary />} />
      <Route path="/library/item/:itemId" element={<StudentLibraryItem />} />
      <Route path="/library/:categoryId" element={<StudentLibraryFolder />} />
      <Route path="/free-videos" element={<StudentFreeVideos />} />
      <Route path="/free-tests" element={<StudentFreeTests />} />
      <Route path="/profile/edit" element={<StudentProfile />} />
      <Route path="/settings" element={<StudentProfile />} />
      <Route path="/settings/:setting" element={<StudentProfile />} />
      <Route path="/video" element={<StudentUtilityPage page="downloads" />} />
      <Route path="/video/:videoId" element={<StudentVideoPlayer />} />
      <Route path="*" element={<Navigate to="/courses" replace />} />
    </Route>
    <Route path="/admin" element={<AdminShell />}>
      <Route index element={<AdminDashboard />} />
      <Route path="courses" element={<AdminCourseReportPage />} />
      <Route path="courses/new" element={<AdminCourseFormPage />} />
      <Route path="courses/folders" element={<AdminCourseFolderReportPage />} />
      <Route path="courses/folders/new" element={<AdminCourseFolderFormPage />} />
      <Route path="subjects" element={<AdminSubjectReportPage />} />
      <Route path="subjects/new" element={<AdminSubjectFormPage />} />
      <Route path="videos/import" element={<AdminVideoPage mode="import" />} />
      <Route path="videos/upload" element={<AdminVideoPage mode="upload" />} />
      <Route path="videos/imported" element={<AdminVideoPage mode="imported" />} />
      <Route path="videos/uploaded" element={<AdminVideoPage mode="uploaded" />} />
      <Route path="live/zoom" element={<AdminLivePage mode="report" />} />
      <Route path="live/zoom/new" element={<AdminLivePage mode="new" />} />
      <Route path="live/youtube" element={<AdminLivePage mode="report" youtube />} />
      <Route path="live/youtube/new" element={<AdminLivePage mode="new" youtube />} />
      <Route path="materials" element={<AdminMaterialReportPage />} />
      <Route path="materials/new" element={<AdminMaterialFormPage />} />
      <Route path="users" element={<AdminUserReportPage />} />
      <Route path="users/new" element={<AdminUserFormPage />} />
      <Route path="subscriptions" element={<AdminSubscriptionPage mode="subscriptions" />} />
      <Route path="subscriptions/transactions" element={<AdminSubscriptionPage mode="transactions" />} />
      <Route path="subscriptions/new" element={<AdminSubscriptionFormPage />} />
      <Route path="misc/materials" element={<AdminMiscMaterialsPage />} />
      <Route path="misc/materials/new" element={<AdminMiscMaterialFormPage />} />
      <Route path="misc/categories" element={<AdminMiscCategoriesPage />} />
      <Route path="misc/categories/new" element={<AdminMiscCategoryFormPage />} />
      <Route path="reports/sales" element={<AdminSaleDetailsPage />} />
      <Route path="settings/banners" element={<AdminContentPage kind="banners" />} />
      <Route path="settings/faqs" element={<AdminContentPage kind="faqs" />} />
      <Route path="settings/about" element={<AdminContentPage kind="about" />} />
      <Route path="settings/terms" element={<AdminContentPage kind="terms" />} />
      <Route path="settings/queries" element={<AdminContentPage kind="queries" />} />
      <Route path="marketing/stories/new" element={<AdminMarketingPage kind="stories" form />} />
      <Route path="marketing/stories" element={<AdminMarketingPage kind="stories" />} />
      <Route path="marketing/coupons/new" element={<AdminMarketingPage kind="coupons" form />} />
      <Route path="marketing/coupons" element={<AdminMarketingPage kind="coupons" />} />
      <Route path="marketing/notifications" element={<AdminMarketingPage kind="notifications" />} />
      <Route path="marketing/social" element={<AdminMarketingPage kind="social" />} />
      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Route>
    <Route path="/test-admin" element={<TestAdminShell />}>
      <Route index element={<TestAdminDashboard />} />
      <Route path="tests" element={<TestAdminTests />} />
      <Route path="tests/new" element={<TestAdminTestEditor />} />
      <Route path="tests/:testId/mapping" element={<TestAdminMapping />} />
      <Route path="tests/:testId" element={<TestAdminTestEditor />} />
      <Route path="test-questions" element={<TestAdminTestQuestionReport />} />
      <Route path="questions" element={<TestAdminQuestionBank />} />
      <Route path="questions/image" element={<TestAdminQuestionBank fixedType="MCQImg" />} />
      <Route path="questions/import" element={<TestAdminQuestionImport />} />
      <Route path="questions/new" element={<TestAdminQuestionForm />} />
      <Route path="questions/image/new" element={<TestAdminQuestionForm mode="image" />} />
      <Route path="questions/image/:questionId" element={<TestAdminQuestionForm mode="image" />} />
      <Route path="questions/:questionId" element={<TestAdminQuestionForm />} />
      <Route path="results" element={<TestAdminResultsParity />} />
      <Route path="question-of-day" element={<TestAdminQuestionOfDayParity />} />
      <Route path="reported-questions" element={<TestAdminReportedQuestionsParity />} />
      <Route path="release" element={<TestAdminRelease />} />
      <Route path="*" element={<Navigate to="/test-admin" replace />} />
    </Route>
  </Routes></BrowserRouter>;
}
